import React, { useState } from 'react';
import { Button, Space, Switch, Tooltip, Typography, message } from 'antd';
import type { OperationalActionDescriptor } from '../routes/execution/executionActionsFacade';
import type { ExecutionCargoHydration } from './types';
import ExecutionActionModal, {
  type ExecutionActionSubmitPayload,
  resolveActionSequence
} from './ExecutionActionModal';
import {
  getExecutionWorkflowMode,
  setExecutionWorkflowMode
} from '../routes/execution/workflowModeFacade';

const { Text } = Typography;

type Props = {
  routeId: number;
  actions: OperationalActionDescriptor[];
  cargoAtStop: ExecutionCargoHydration[];
  onSubmit: (payload: ExecutionActionSubmitPayload) => Promise<void>;
  onOpenCompletion?: () => void;
  loading?: boolean;
  disabled?: boolean;
  showModeToggle?: boolean;
  onModeChange?: () => void;
};

export const OperationalExecutionActions: React.FC<Props> = ({
  routeId,
  actions,
  cargoAtStop,
  onSubmit,
  onOpenCompletion,
  loading = false,
  disabled = false,
  showModeToggle = true,
  onModeChange
}) => {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [activeDescriptor, setActiveDescriptor] = useState<OperationalActionDescriptor | null>(null);
  const [advancedMode, setAdvancedMode] = useState(getExecutionWorkflowMode() === 'advanced');

  if (actions.length === 0 && !showModeToggle) return null;

  const handleClick = (descriptor: OperationalActionDescriptor) => {
    if (descriptor.disabled) return;
    if (descriptor.key === 'complete_route' && onOpenCompletion) {
      onOpenCompletion();
      return;
    }
    if (descriptor.key === 'in_transit' && descriptor.disabled) return;
    setActiveDescriptor(descriptor);
  };

  const handleModalSubmit = async (payload: ExecutionActionSubmitPayload) => {
    setPendingKey(`${payload.descriptor.key}:${payload.descriptor.executionStopUid ?? 'route'}`);
    try {
      await onSubmit(payload);
      message.success(`Выполнено: ${payload.descriptor.label}`);
      setActiveDescriptor(null);
    } catch (e) {
      const err = e as { data?: { error?: string }; message?: string };
      message.error(err?.data?.error ?? err?.message ?? 'Не удалось выполнить операцию');
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <>
      {showModeToggle ? (
        <Space style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Режим:
          </Text>
          <Switch
            size="small"
            checked={advancedMode}
            checkedChildren="Расшир."
            unCheckedChildren="Простой"
            onChange={(checked) => {
              setAdvancedMode(checked);
              setExecutionWorkflowMode(checked ? 'advanced' : 'simple');
              onModeChange?.();
            }}
          />
        </Space>
      ) : null}

      {actions.length > 0 ? (
        <Space wrap size={[8, 8]}>
          {actions.map((descriptor) => {
            const key = `${descriptor.key}:${descriptor.executionStopUid ?? 'route'}`;
            const canOpen =
              descriptor.opensIncident ||
              descriptor.key === 'complete_route' ||
              resolveActionSequence(descriptor).length > 0 ||
              !descriptor.disabled;

            const btn = (
              <Button
                key={key}
                size="small"
                type={descriptor.disabled ? 'default' : descriptor.opensIncident ? 'default' : 'primary'}
                danger={descriptor.opensIncident}
                ghost={!descriptor.disabled && !descriptor.opensIncident}
                disabled={disabled || (descriptor.disabled && descriptor.key !== 'complete_route') || !canOpen}
                loading={loading && pendingKey === key}
                onClick={() => handleClick(descriptor)}
              >
                {descriptor.label}
              </Button>
            );
            return descriptor.hint ? (
              <Tooltip key={key} title={descriptor.hint}>
                {btn}
              </Tooltip>
            ) : (
              btn
            );
          })}
        </Space>
      ) : null}

      <ExecutionActionModal
        open={activeDescriptor != null}
        descriptor={activeDescriptor}
        routeId={routeId}
        cargoAtStop={cargoAtStop}
        loading={loading}
        onCancel={() => setActiveDescriptor(null)}
        onSubmit={handleModalSubmit}
      />
    </>
  );
};

export default OperationalExecutionActions;
